import { css } from '@emotion/css'
import { ResizableNodeView } from '@tiptap/core'
import type { NodeView } from '@tiptap/pm/view'
import { NodeViewRendererProps } from '@tiptap/react'
import { TextEditorAsset } from '../../types'
import * as styles from './styles'
import { getImageAspectRatio, getMaxWidth, getMinDimensions } from './utils'

export const resizableImageView = (
  baseNodeView: NodeView,
  props: NodeViewRendererProps
): NodeView => {
  const { node, editor, extension } = props
  const asset = editor.storage.asset.assetStore.find(
    (a: TextEditorAsset) => a.assetUuid === node.attrs.assetUuid
  )

  const aspectRatio = getImageAspectRatio(asset, {
    width: node.attrs.width,
    height: node.attrs.height,
  })
  const minDimensions = getMinDimensions(aspectRatio)
  const maxWidth = getMaxWidth(editor.view.dom)
  const element = baseNodeView.dom as HTMLElement

  const resizableNodeView = new ResizableNodeView({
    editor,
    element,
    node,
    getPos: props.getPos,
    onResize: (width, height) => {
      if (element) {
        element.style.width = `${width}px`
        element.style.height = `${height}px`
      }
    },
    onCommit: (width, height) => {
      const pos = props.getPos()
      if (pos === undefined) return

      editor
        .chain()
        .focus()
        .setNodeSelection(pos)
        .updateAttributes(extension.name, { width, height })
        .run()
    },
    onUpdate: (updatedNode) => {
      if (updatedNode.type !== node.type) return false
      return true
    },
    options: {
      directions: ['top-right', 'top-left', 'bottom-right', 'bottom-left'],
      min: minDimensions,
      max: { width: maxWidth },
      preserveAspectRatio: true,
      className: {
        container: css(styles.resizeContainer),
        resizing: 'is-resizing',
      },
    },
  })

  return {
    ...baseNodeView,
    dom: resizableNodeView.dom,
    update: (updatedNode, decorations, innerDecorations) => {
      if (updatedNode.type !== node.type) return false
      baseNodeView.update?.(updatedNode, decorations, innerDecorations)
      return true
    },
    destroy: () => {
      resizableNodeView.destroy?.()
      baseNodeView.destroy?.()
    },
  }
}
